import { LayeredToast } from '@/components/hooks/useToast';
import { Button, ButtonVariants } from '@triumph/design-system';
import { Toast, ToastMessage } from 'primereact/toast';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
    SuccessBannerActions,
    SuccessBannerBody,
    SuccessBannerTitle,
} from './SuccessBanner.StyledComponents';

export const SUCCESS_BANNER_SECONDS = 5;

interface SuccessBannerProps {
    /** Test ids are `${testIdPrefix}-title`, `-dismiss` and `-view-now`. */
    testIdPrefix: string;
    rootTestId?: string;
    title: ReactNode;
    /** The description block between the title and the buttons. */
    children?: ReactNode;
    onViewNow: () => void;
    onDismiss: () => void;
}

/** The sticky "created" banner: title, text, and a Dismiss button that counts down and pauses on hover. */
export const SuccessBanner = ({
    testIdPrefix,
    rootTestId = testIdPrefix,
    title,
    children,
    onViewNow,
    onDismiss,
}: SuccessBannerProps) => {
    const [secondsLeft, setSecondsLeft] = useState(SUCCESS_BANNER_SECONDS);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        if (isPaused) return;
        if (secondsLeft <= 0) {
            onDismiss();
            return;
        }
        const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, isPaused, onDismiss]);

    return (
        <SuccessBannerBody
            data-testid={rootTestId}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <SuccessBannerTitle data-testid={`${testIdPrefix}-title`}>{title}</SuccessBannerTitle>
            {children}
            <SuccessBannerActions>
                <Button
                    variant={ButtonVariants.secondary}
                    onClick={onDismiss}
                    data-testid={`${testIdPrefix}-dismiss`}
                >
                    Dismiss ⏳ {secondsLeft} sec
                </Button>
                <Button
                    variant={ButtonVariants.primary}
                    onClick={onViewNow}
                    data-testid={`${testIdPrefix}-view-now`}
                >
                    View Now →
                </Button>
            </SuccessBannerActions>
        </SuccessBannerBody>
    );
};

let bannerSeq = 0;

/** Bottom-right stack of sticky banners. `render` gets the function that closes the banner it builds. */
export const useSuccessBannerToast = (testId: string, idPrefix: string) => {
    const toastRef = useRef<Toast>(null);

    const showBanner = (render: (dismiss: () => void) => ReactNode) => {
        bannerSeq += 1;
        const id = `${idPrefix}-${bannerSeq}`;
        const dismiss = () => toastRef.current?.remove(message);
        // Toast.remove deep-compares keys last-first; keep `id` last so it short-circuits before `content`.
        const message: ToastMessage = {
            severity: 'success',
            sticky: true,
            closable: false,
            contentStyle: { padding: 0 },
            content: render(dismiss),
            id,
        };
        toastRef.current?.show(message);
    };

    const banner = (
        <LayeredToast
            ref={toastRef}
            position="bottom-right"
            appendTo={document.body}
            pt={{ root: { style: { width: '25rem', maxWidth: '25vw' } } }}
            data-testid={testId}
        />
    );

    return { banner, showBanner };
};
